import { Box, Button, Flex, Text, Tooltip, VStack } from '@chakra-ui/react';
import axios from 'axios';

import Breadcrumbs from 'components/common/Breadcrumbs';
import FormikFormControl from 'components/common/forms/FormikFormControl';
import debounce from 'lodash.debounce';
import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';

import { useDispatch, useSelector } from 'react-redux';
import { getLearningPlansAction } from 'store/modules/learningPlan/learningPlan-action';

import * as Yup from 'yup';
import ClassForm from 'components/class/ClassForm';
import UserTable from 'components/class/UserTable';
import SearchGroupMd from 'components/class/modals/SearchGroupMd';
import SelectUserInput from 'components/common/forms/SelectUserInput';
import { userBaseColumns, userRemoveColumn } from 'utils/columns/userColumns';
import ResultModal from 'components/common/modals/ResultModal';
import { ReducerStatus } from 'utils/constants';
import { ADMIN, TRAINER } from 'utils/constants/roles';
import { useNavigate } from 'react-router-dom';
import { createClassAction } from 'store/modules/class/class-action';
import { classActions } from 'store/modules/class/class-slice';

function ClassCreatePage() {
  document.title = `Skill Quest | Admin - Create Class`;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accounts } = useMsal();

  // Safe access to user roles with proper null/undefined checks
  const userAccount = accounts?.[0];
  const idTokenClaims = userAccount?.idTokenClaims;
  const roles = idTokenClaims?.roles || [];

  // Check if user has admin or trainer role for admin access
  const hasAdminOrTrainerRole = roles.some((role) =>
    [ADMIN, TRAINER].includes(role)
  );

  /**
   * Permission-based redirect for admin access control
   * If user doesn't have ADMIN or TRAINER role, redirect to non-admin classes list
   * Uses replace: true to prevent back navigation to unauthorized page
   */
  useEffect(() => {
    if (!hasAdminOrTrainerRole) {
      navigate('/classes', { replace: true });
    }
  }, [hasAdminOrTrainerRole, navigate]);

  const createClass = useSelector((state) => state.class.createClass);
  const newClass = useSelector((state) => state.class.class);

  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const formInputs = {
    name: '',
    description: '',
    learningPlan: null,
    costCenter: null,
    dateStart: '',
    dateEnd: '',
  };

  const loadLearningPlans = debounce((inputValue, callback) => {
    axios
      .get(`/admin/learning-plans?query=${inputValue.trim()}&pageSize=${20}`)
      .then((response) => {
        callback(
          response.data.data?.map((item) => ({
            label: item.name,
            value: item.id,
          }))
        );
      })
      .catch(() => {
        callback([]);
      });
  }, 1000);

  const formValidation = Yup.object({
    name: Yup.string().max(155).required('Required'),
    description: Yup.string().max(1000).required('Required'),
    learningPlan: Yup.object().required('Required'),
    costCenter: Yup.string().nullable().max(20),
    dateStart: Yup.date().required('Required'),
    dateEnd: Yup.date()
      .min(
        Yup.ref('dateStart'),
        'End date must be greater than or equal to start date'
      )
      .required('Required'),
  });

  const handleRemoveUser = (emailToRemove) => {
    setUsers((prevList) =>
      prevList.filter((item) => item.value !== emailToRemove)
    );
  };

  const handleRemoveSelectedUsers = () => {
    setUsers((prevUsers) =>
      prevUsers.filter(
        (user) =>
          !selectedUsers.some(
            (selectedUser) => selectedUser.value === user.value
          )
      )
    );
  };

  const handleAddGroupMembers = (members) => {
    setUsers((prevList) => [
      ...prevList,
      ...members.filter(
        (item2) => !prevList.some((item1) => item1.value === item2.value)
      ),
    ]);
  };

  const handleSubmiForm = (submittedForm) => {
    const formData = new FormData();

    Object.entries(submittedForm).forEach(([key, value]) => {
      if (key !== 'file' && key !== 'emails' && key !== 'learningPlan')
        formData.append(key, value);
    });

    users.forEach((user) => formData.append('UserEmails', user.value));

    submittedForm.emails.forEach((group) =>
      formData.append('OwnerGroups', group)
    );

    formData.append('LearningPlanId', submittedForm.learningPlan.value);

    if (submittedForm.file && submittedForm.file.name) {
      formData.append('Thumbnail', submittedForm.file, submittedForm.file.name);
    }

    dispatch(createClassAction(formData));
  };

  const handleResult = () => {
    dispatch(classActions.setCreateClass({ status: ReducerStatus.IDLE }));
    if (createClass.status === ReducerStatus.SUCCEEDED)
      navigate(`/admin/classes/${newClass.id}`);
  };

  useEffect(() => {
    dispatch(getLearningPlansAction({ currentPage: 1, pageSize: 10 }));
  }, [dispatch]);

  return (
    <Box ml={{ base: 0, md: 20 }} minH="100vh" p="10">
      <ResultModal
        isOpen={
          createClass.status === ReducerStatus.SUCCEEDED ||
          createClass.status === ReducerStatus.FAILED
        }
        onSubmit={handleResult}
        isError={createClass.status === ReducerStatus.FAILED}
        title={
          createClass.status === ReducerStatus.SUCCEEDED
            ? 'Class Created'
            : null
        }
        message={
          createClass.status === ReducerStatus.SUCCEEDED
            ? 'The class has been created successfully.'
            : createClass.error?.data?.title || 'Server Error'
        }
        errorObject={createClass.error?.data?.errors}
      />

      <Breadcrumbs
        mb={4}
        links={[
          { name: 'Classes', path: '/admin/classes' },
          {
            name: 'Create',
            path: '/admin/classes/create',
          },
        ]}
      />
      <Text flex={1} fontSize="2xl">
        Create Class
      </Text>

      <Flex p={5} direction={{ base: 'column', md: 'row' }} gap={5}>
        <ClassForm
          flex={1}
          resetTrigger={0}
          status={createClass.status}
          formInputs={formInputs}
          formValidation={formValidation}
          onSubmitHandler={handleSubmiForm}
          loadLearningPlans={loadLearningPlans}
        />

        <VStack flex={1} gap={5} align="start">
          <FormikFormControl
            label="Select Users"
            helperText="Please select the users who will be participating in this class. You may leave this blank and add users after the class has been created."
          >
            <VStack gap={5} align="stretch">
              <SelectUserInput
                value={users}
                isMulti
                isClearable
                onChangeHandler={setUsers}
              />

              <SearchGroupMd handleAdd={handleAddGroupMembers} />
            </VStack>
          </FormikFormControl>

          <UserTable
            users={users}
            columns={[...userBaseColumns, userRemoveColumn(handleRemoveUser)]}
            onSelectRowHandler={setSelectedUsers}
          >
            <Flex my={2} justify="end">
              <Tooltip
                label={selectedUsers.length === 0 ? 'Select user first' : ''}
              >
                <Button
                  isDisabled={selectedUsers.length === 0}
                  colorScheme="red"
                  size="xs"
                  onClick={handleRemoveSelectedUsers}
                >
                  Remove
                </Button>
              </Tooltip>
            </Flex>
          </UserTable>
        </VStack>
      </Flex>
    </Box>
  );
}

export default ClassCreatePage;
